import _ from "lodash";
import React, { useState, useCallback, Fragment } from "react";
import { connect } from "react-redux";

import NoEvidenceView from "./NoEvidenceView";
import EvidenceFeedHeader from "./EvidenceFeedHeader";
import classes from "./CardContainer.scss";
import { Alert } from "@toddle-edu/ds-web";
import PostSelection from "AppComponents/PostSelection";
import UnitSelection from "AppComponents/UnitSelection";
import AssessmentSelection from "AppComponents/AssessmentSelection";
import AssignmentSelection from "AppComponents/AssignmentSelection";
import OrganizationResourceSelection from "AppComponents/OrganizationResourceSelection";

import Filters from "./Filters";
import Loading from "UIComponents/Loading";
import { getCurriculumProgramIdFromState } from "Utils";
import EvidenceCard from "./EvidenceCard";
import ResourceCard from "./ResourceCard";
import {
  evidenceFeedStyles,
  unitPlanFeedStyles,
  assessmentFeedStyles,
  assignmentFeedStyles,
} from "./CardContainerStyles";

const CardContainer = props => {
  const {
    activeTabConfig,
    activeTab,
    selectedCards,
    changeSelectedCards,
    activeTabEvidenceTypes,
    curriculumProgramId,
    parentIdConfig,
    onClickView,
    customEvidences,
    initialAppliedFilters,
    selectAllOptionsInitially,
    curriculumType,
    t,
    isProjectNode,
    projectNodeDetails,
    noFiltersApplicable,
    testId,
  } = props;

  const [filterObj, setFilterObj] = useState({});

  const [selectedFilterItemsLocale, setSelectedFilterItemsLocale] = useState(
    {}
  );

  const [filterStatusObj, setFilterStatusObj] = useState({});

  const updateFilterStatus = useCallback(
    ({ loaded }) => {
      setFilterStatusObj(prev => ({
        ...prev,
        [activeTab]: loaded,
      }));
    },
    [activeTab]
  );

  const updateFilterItemsLocaleForActiveTab = useCallback(
    ({ data }) => {
      setSelectedFilterItemsLocale(selectedFilterItemsLocale => {
        return { ...selectedFilterItemsLocale, [activeTab]: data };
      });
    },
    [activeTab]
  );

  const updateFilterObject = useCallback(
    ({ newFilterObj }) => {
      setFilterObj(filterObj => {
        return { ...filterObj, [activeTab]: newFilterObj };
      });
    },
    [activeTab]
  );

  const selectedEvidence = _.get(selectedCards, [activeTab], []);

  const updateSelectedEvidence = updatedEvidence => {
    changeSelectedCards(prev => {
      return {
        ...prev,
        [activeTab]: updatedEvidence,
      };
    });
  };

  const parentId = _.get(parentIdConfig, activeTab, "");
  const parentType = _.get(activeTabConfig, "parentType", "");

  const activeTabFilters = _.get(filterObj, activeTab, {});
  const configFilters = activeTabConfig?.filters ?? {};
  const filters = { ...activeTabFilters, ...configFilters };

  /**mark filters as ready when filters are loaded for current tab */
  const areFiltersLoaded = filterStatusObj[activeTab];

  const openEvidenceView = ({ data, evidenceType }) => {
    const type = evidenceType ? evidenceType : _.first(activeTabEvidenceTypes);
    onClickView({ evidenceType: type, data });
  };

  const getFeedHeaderLabel = () => {
    switch (activeTab) {
      case "SNS":
        return t("snp:add_subject_standard_evidence");
      default:
        return activeTabConfig.label;
    }
  };

  const evidenceFeedProps = {
    parentId,
    parentType,
    curriculumProgramId,
    customEmptyViewElement: <NoEvidenceView />,
    customHeaderElement: (
      <EvidenceFeedHeader
        t={t}
        label={getFeedHeaderLabel()}
        selectedFiltersItems={selectedFilterItemsLocale[activeTab]}
      />
    ),
    updateSelectedItems: updateSelectedEvidence,
    selectedItems: selectedEvidence,
  };

  const getCurrentTabEvidenceFeed = () => {
    switch (activeTab) {
      case "UNIT_EVIDENCE": {
        const { alertMessage } = activeTabConfig;
        return (
          <>
            {alertMessage ? (
              <div className={classes.unitEvidenceAlertContainer}>
                <Alert
                  dsVersion="2.0"
                  showIcon={false}
                  type={alertMessage.type}
                  message={alertMessage.message}
                />
              </div>
            ) : null}
            <PostSelection
              filters={filters}
              {...evidenceFeedProps}
              styles={evidenceFeedStyles}
              customCardComponent={
                <EvidenceCard
                  type="UNIT_EVIDENCE"
                  onClickMedia={openEvidenceView}
                />
              }
              isMultiSelect={true} //provide support for single item selection when needed
              testId={
                testId ? `${testId}-cardContainer-unitEvidence` : undefined
              }
            />
          </>
        );
      }
      case "REFLECTION_POST":
      case "PROGRESS_REPORT_POST": {
        return (
          <PostSelection
            filters={filters}
            {...evidenceFeedProps}
            styles={evidenceFeedStyles}
            customCardComponent={
              <EvidenceCard
                type="UNIT_EVIDENCE"
                onClickMedia={openEvidenceView}
              />
            }
            isMultiSelect={true} //provide support for single item selection when needed
            isProjectNode={isProjectNode}
            projectNodeDetails={projectNodeDetails}
            testId={
              testId ? `${testId}-cardContainer-progressReportPost` : undefined
            }
          />
        );
      }
      case "UNIT_PLANS": {
        return (
          <UnitSelection
            filters={filters}
            queryType={activeTabConfig.queryType}
            shouldGroupScheduledFeed={activeTabConfig.shouldGroupScheduledFeed}
            styles={unitPlanFeedStyles}
            customCardComponent={
              <EvidenceCard type="UNIT_PLANS" onClickMedia={openEvidenceView} />
            }
            isMultiSelect={true}
            showGrades={activeTabConfig.showGrades}
            showSubTitleTooltip={activeTabConfig.showSubTitleTooltip}
            {...evidenceFeedProps}
            testId={testId ? `${testId}-cardContainer-unitPlans` : undefined}
          />
        );
      }
      case "LEARNING_EXPERIENCES": {
        return (
          <AssessmentSelection
            cardType={"boxCards"}
            {...filters}
            type={activeTabConfig.type}
            isMultiSelect={true}
            customCardComponent={
              <ResourceCard
                onClickMedia={openEvidenceView}
                type="LEARNING_EXPERIENCES"
              />
            }
            styles={assessmentFeedStyles}
            first={25}
            {...evidenceFeedProps}
            testId={
              testId ? `${testId}-cardContainer-learningExperiences` : undefined
            }
          />
        );
      }
      case "CLASSROOM":
        return (
          <AssignmentSelection
            filters={filters}
            orderBy={activeTabConfig.orderBy}
            orderByDirection={activeTabConfig.orderByDirection}
            styles={assignmentFeedStyles}
            customCardComponent={
              <ResourceCard onClickMedia={openEvidenceView} type="CLASSROOM" />
            }
            first={25}
            {...evidenceFeedProps}
            testId={testId ? `${testId}-cardContainer-classroom` : undefined}
          />
        );
      case "SCHOOL_POLICY":
      case "POLICIES_AND_RESOURCES":
      case "TEACHER_RESOURCES": {
        return (
          <OrganizationResourceSelection
            {...evidenceFeedProps}
            filters={filters}
            styles={evidenceFeedStyles}
            customCardComponent={
              <EvidenceCard
                type="ORGANIZATION_RESOURCES"
                onClickMedia={openEvidenceView}
              />
            }
            cardType={"VIEW_AND_SELECT"}
            types={activeTabEvidenceTypes}
            visibleTo={activeTabConfig.visibleTo}
            testId={testId}
          />
        );
      }
      default: {
        const elementKey = _.get(activeTabConfig, "elementKey", "");

        const activeTabElement = _.get(customEvidences, elementKey, null);

        return activeTabElement
          ? React.cloneElement(activeTabElement, {
              ...evidenceFeedProps,
              filters,
              customCardComponent: (
                <ResourceCard
                  onClickMedia={openEvidenceView}
                  type={elementKey}
                />
              ),
              testId: testId,
            })
          : null;
      }
    }
  };

  return (
    <Fragment>
      <Filters
        initialAppliedFilters={initialAppliedFilters}
        selectAllOptionsInitially={selectAllOptionsInitially}
        updateFilterObject={updateFilterObject}
        activeTab={activeTab}
        updateFilterItemsLocaleForActiveTab={
          updateFilterItemsLocaleForActiveTab
        }
        filterStatus={filterStatusObj[activeTab] ?? false}
        updateFilterStatus={updateFilterStatus}
        curriculumProgramId={curriculumProgramId}
        curriculumType={curriculumType}
        isProjectNode={isProjectNode}
        projectNodeDetails={projectNodeDetails}
        noFiltersApplicable={noFiltersApplicable}
        testId={testId}
      />

      {areFiltersLoaded ? getCurrentTabEvidenceFeed() : <Loading />}
    </Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType = _.get(ownProps, "curriculumProgramType", "");
  const curriculumProgramId =
    _.get(ownProps, "curriculumProgramId") ||
    getCurriculumProgramIdFromState({ state });

  return {
    curriculumProgramId,
    curriculumType,
  };
};

export default connect(mapStateToProps)(CardContainer);
